import {useCallback, useEffect, useRef} from 'react'
import {recortesPorPrecargar, type TarjetaCola} from '@/lib/bateria-cola'
import {marcarRecorteCaido} from '@/lib/recortes-caidos'

// Sin tope, la ventana compite con el recorte en pantalla; cinco es lo que el navegador sostiene
// contra un mismo host sin encolar en el socket.
const SIMULTANEAS = 5

// Caché de decode, no de red: soltar el `HTMLImageElement` obliga a redecodificar al volver.
// Sesenta cubren la ventana de ±16 con margen para ir y volver.
const VIVAS = 60

/** Precalienta con `decode()` los recortes alrededor del cursor, lo más cercano primero; un rechazo
 * marca el recorte caído. `calentar(url)` suma una URL externa al final de la misma cola. */
export function usePrecargaRecortes(tarjetas: TarjetaCola[], indice: number): (url: string) => void {
  const pedidas = useRef<Set<string>>(new Set())
  // Un `Map` conserva el orden de inserción: la primera clave es la más vieja.
  const vivas = useRef<Map<string, HTMLImageElement>>(new Map())
  const pendientes = useRef<string[]>([])
  const enVuelo = useRef(0)
  // Indirección por ref: el bombeo se reinvoca al liberar un cupo y un `useCallback` no puede nombrarse.
  const bombearRef = useRef<() => void>(() => {})

  const bombear = useCallback(() => {
    while (enVuelo.current < SIMULTANEAS && pendientes.current.length > 0) {
      const url = pendientes.current.shift()
      if (url === undefined) return
      enVuelo.current += 1
      const imagen = new Image()
      imagen.src = url
      const soltar = () => {
        vivas.current.set(url, imagen)
        const sobrantes = vivas.current.size - VIVAS
        for (let i = 0; i < sobrantes; i += 1) {
          const masVieja = vivas.current.keys().next().value
          if (masVieja !== undefined) vivas.current.delete(masVieja)
        }
        enVuelo.current -= 1
        bombearRef.current()
      }
      const decodificada = typeof imagen.decode === 'function' ? imagen.decode() : Promise.resolve()
      // El rechazo anticipa que el recorte ya no existe, para que la tarjeta baje de peldaño antes
      // de que el cursor llegue.
      decodificada.then(soltar, () => {
        marcarRecorteCaido(url)
        soltar()
      })
    }
  }, [])

  useEffect(() => {
    bombearRef.current = bombear
  }, [bombear])

  useEffect(() => {
    const urls = recortesPorPrecargar(tarjetas, indice, pedidas.current)
    if (urls.length === 0) return
    for (const url of urls) pedidas.current.add(url)
    pendientes.current = [...urls, ...pendientes.current]
    bombear()
  }, [tarjetas, indice, bombear])

  const calentar = useCallback(
    (url: string) => {
      // Basta `pedidas`: toda URL de `vivas` pasó antes por ahí y `pedidas` no se vacía.
      if (pedidas.current.has(url)) return
      pedidas.current.add(url)
      pendientes.current.push(url)
      bombear()
    },
    [bombear]
  )

  return calentar
}
